import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Send } from 'lucide-react';
import { Button, Card } from '@/components/ui';
import type { PublishChannel } from '@/api/publishChannels';
import { sendListingsToChannels } from '@/api/publishChannels';
import { describeApiError } from '@/lib/api-errors';
import { cn } from '@/lib/utils';

interface SendToChannelBarProps {
  selectedDesignIds: string[];
  channels: PublishChannel[];
  onSent: () => void;
}

/**
 * Bulk action of the overview (plan U10).
 *
 * Reports how many jobs were enqueued rather than claiming success: listings
 * that already sit on the chosen channel are skipped server-side, so "0
 * enqueued" is a normal answer the seller needs to see.
 */
export function SendToChannelBar({
  selectedDesignIds,
  channels,
  onSent,
}: SendToChannelBarProps) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);

  const toggle = (channelId: string) => {
    setPicked((current) =>
      current.includes(channelId)
        ? current.filter((id) => id !== channelId)
        : [...current, channelId]
    );
  };

  const send = async () => {
    setIsSending(true);
    try {
      const result = await sendListingsToChannels(selectedDesignIds, picked);
      toast.success(
        t('seller.designs.send.result', { count: result?.enqueued ?? 0 })
      );
      setPicked([]);
      onSent();
    } catch (error) {
      toast.error(describeApiError(error));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card
      padding="sm"
      className="flex flex-wrap items-center gap-3"
      role="region"
      aria-label={t('seller.designs.send.title')}
    >
      <span className="text-sm font-medium text-ink">
        {t('seller.designs.send.selected', { count: selectedDesignIds.length })}
      </span>

      <div className="flex flex-wrap gap-2">
        {channels.map((channel) => (
          <button
            key={channel.id}
            type="button"
            onClick={() => toggle(channel.id)}
            aria-pressed={picked.includes(channel.id)}
            className={cn(
              'rounded-chip border px-3 py-1 text-xs transition',
              picked.includes(channel.id)
                ? 'border-primary bg-[color-mix(in_srgb,var(--primary)_12%,transparent)] text-ink'
                : 'border-line text-mute hover:border-primary'
            )}
          >
            {channel.displayName}
          </button>
        ))}
      </div>

      <Button
        variant="primary"
        size="sm"
        disabled={picked.length === 0}
        loading={isSending}
        loadingLabel={t('seller.designs.send.sending')}
        onClick={() => void send()}
      >
        <Send className="h-4 w-4" aria-hidden="true" />
        {t('seller.designs.send.action')}
      </Button>
    </Card>
  );
}
